/**
 * LandingPage.js
 *
 * Rendered at "/" for logged-out visitors (see RootRedirect in App.js) —
 * this is what replaces the old immediate bounce-to-/login behavior.
 * Logged-in users never see this; RootRedirect still sends them straight
 * to their dashboard as before.
 *
 * Locked-condition flow: clicking Get Started stashes the chosen
 * condition in localStorage (LOCKED_CONDITION_KEY) before navigating to
 * /register — consumed exactly once, on the patient's first dashboard
 * load after registration (see PatientPortal.js), to auto-launch the Add
 * Condition flow pre-locked to that one condition instead of the normal
 * free-choice picker. Query params aren't used for this (RegisterPage.js
 * doesn't read them, and they wouldn't survive the redirect to the
 * dashboard after registration completes anyway).
 *
 * Pricing displayed here (mrp / offer) is fetched live from
 * GET /public/pricing (Sept 2026) — condition_fees.mrp_paise / base_fee_paise
 * minus the standing default discount, computed fresh on every load. The
 * mrp/offer numbers on each MAIN_OPTIONS/OTHER_OPTION entry below are now
 * only a fallback, used if that fetch fails for any reason (so the page
 * never shows a blank/broken price) — they are NOT the source of truth
 * and don't need hand-updating when a fee changes anymore.
 *
 * "Other Problems" (added Sept 2026, moved off the main grid onto its
 * own `view` — see the "click here" line below the 3 main cards): its
 * condition_fees row and the rest of its backend wiring already exist
 * (see conditionController.js/routes/migration from that session).
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { publicAPI } from '../api';
import { LegalFooter } from './LegalDocuments';
import { MascotLogo } from './common/ThyroDoctorMascot'; // mascot beside the header wordmark (4 Oct 2026)
import wingLeft from '../assets/butterfly-diamond-left.png';
import wingRight from '../assets/butterfly-diamond-right.png';

// English / Hindi (13 Oct 2026, Hindi version Batch 1b)
import { useI18n, LanguageSwitcher } from '../i18n';
const LOCKED_CONDITION_KEY = 'thyro_locked_condition';

const MAIN_OPTIONS = [
  {
    key: 'disorder',
    eyebrow: 'Seek Opinion For', // was 'Get Opinion For' (13 Oct 2026)
    title: 'Thyroid Disorder',
    description: "Hypothyroidism or Hyperthyroidism - get opinion on your symptoms and hormone level status",
    mrp: 2000,
    offer: 699,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '100%', height: '100%' }}>
        <path d="M20 8.04l-12.122 12.124a2.857 2.857 0 1 1 -4.041 -4.04l12.122 -12.124" />
        <path d="M7 13h8" />
        <path d="M19 15l1.5 1.6a2 2 0 1 1 -3 0l1.5 -1.6z" />
        <path d="M15 3l6 6" />
      </svg>
    ),
    // Two real backend conditions share this one card — clicking "Get
    // started" reveals both so the person picks the one that applies.
    subChoices: [
      { label: 'Hypothyroidism', condition: 'hypothyroidism' },
      { label: 'Hyperthyroidism', condition: 'hyperthyroidism' },
    ],
  },
  {
    key: 'nodule',
    eyebrow: 'Seek Opinion For',
    title: 'Thyroid Nodule',
    description: 'Found a lump in neck or a nodule in thyroid on ultrasound? Get opinion on next steps.',
    mrp: 3000,
    offer: 899,
    icon: (
      <svg viewBox="0 0 200 200" fill="none" stroke="currentColor" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" style={{ width: '100%', height: '100%' }}>
        <path d="M93,125 L107,125 C112,142 122,162 138,168 C156,177 176,170 185,150 C194,129 192,102 180,80 C171,63 170,48 159,37 C148,26 129,27 119,43 C112,70 109,85 107,104 L93,104 C91,85 88,70 81,43 C71,27 52,26 41,37 C30,48 29,63 20,80 C8,102 6,129 15,150 C24,170 44,177 62,168 C78,162 88,142 93,125 Z" />
        <circle cx="35" cy="140" r="16" fill="currentColor" stroke="none" />
      </svg>
    ),
    condition: 'nodule',
  },
  {
    key: 'cancer',
    eyebrow: 'Seek Opinion For',
    title: 'Thyroid Cancer',
    description: 'Already diagnosed or on follow-up - get second opinion on your care plan without fear.',
    mrp: 4000,
    offer: 999,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '100%', height: '100%' }}>
        <defs>
          <linearGradient id="tcRibbonGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#7F77DD" />
            <stop offset="50%" stopColor="#5DCAA5" />
            <stop offset="100%" stopColor="#D4537E" />
          </linearGradient>
        </defs>
        <path stroke="url(#tcRibbonGrad)" d="M7 21s9.286 -9.841 9.286 -13.841a3.864 3.864 0 0 0 -1.182 -3.008a4.13 4.13 0 0 0 -3.104 -1.144a4.13 4.13 0 0 0 -3.104 1.143a3.864 3.864 0 0 0 -1.182 3.01c0 4 9.286 13.84 9.286 13.84" />
      </svg>
    ),
    condition: 'thyroid_cancer',
  },
];

// Reached via the "click here" line below the 3 main cards, not shown in
// that grid itself — see the `view` state in LandingPage below.
const OTHER_OPTION = {
  key: 'other',
  eyebrow: 'Seek Opinion For',
  title: 'Other Problems',
  description: 'Any other health problem you would like a specialist opinion on, unrelated to the thyroid.',
  mrp: 5000,
  offer: 2499,
  icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '100%', height: '100%' }}>
      <path d="M12 3l8 3v6c0 4.5 -3.2 7.6 -8 9c-4.8 -1.4 -8 -4.5 -8 -9v-6l8 -3z" />
      <path d="M12 8v6" />
      <path d="M9 11h6" />
    </svg>
  ),
  condition: 'other',
};

const PriceBlock = ({ mrp, offer }) => {
  const { t } = useI18n();
  return (
  <div style={{ marginBottom: 14 }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, justifyContent: 'center' }}>
      <span style={{ fontSize: 13, color: 'var(--text-primary)', textDecoration: 'line-through', textDecorationColor: 'var(--text-tertiary)' }}>
        ₹{mrp.toLocaleString('en-IN')}/-
      </span>
      <span style={{ fontSize: 21, fontWeight: 700, color: 'var(--teal-600)' }}>
        ₹{offer.toLocaleString('en-IN')}/-
      </span>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t('only')}</span>
    </div>
  </div>
);
};

const OptionCard = ({ opt, price, expandedDisorder, setExpandedDisorder }) => {
  const { t } = useI18n();
  return (
  <div style={{
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg, 14px)',
    padding: '24px 18px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
  }}>
    <div style={{ width: 34, height: 34, marginBottom: 18, color: 'var(--teal-600)' }}>
      {opt.icon}
    </div>

    <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 2px' }}>{t(opt.eyebrow)}</p>
    <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 19, margin: '0 0 10px' }}>
      {t(opt.title)}
    </h2>
    <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-secondary)', margin: '0 0 4px', flex: 1 }}>
      {t(opt.description)}
    </p>

    <PriceBlock mrp={price.mrp} offer={price.offer} />

    {opt.subChoices ? (
      expandedDisorder ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
          {opt.subChoices.map(sc => (
            <Link
              key={sc.condition}
              to="/register"
              onClick={() => localStorage.setItem(LOCKED_CONDITION_KEY, sc.condition)}
              className="btn btn-primary"
              style={{ width: '100%', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
              {t(sc.label)}
            </Link>
          ))}
        </div>
      ) : (
        <button
          type="button"
          className="btn btn-primary"
          style={{ width: '100%', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          onClick={() => setExpandedDisorder(true)}
        >
          {t('Get Opinion Now')}
        </button>
      )
    ) : (
      <Link
        to="/register"
        onClick={() => localStorage.setItem(LOCKED_CONDITION_KEY, opt.condition)}
        className="btn btn-primary"
        style={{ width: '100%', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        {t('Get Opinion Now')}
      </Link>
    )}
  </div>
);
};

const LandingPage = () => {
  const { t } = useI18n();
  const [expandedDisorder, setExpandedDisorder] = useState(false);
  // 'main' = the 3 thyroid cards + the "click here" line; 'other' = the
  // single Other Problems card, reached via that line. Client-side only —
  // no route change, no URL for this second view.
  const [view, setView] = useState('main');
  // Live pricing overrides each card's hardcoded mrp/offer once fetched —
  // keyed by condition_fees' own short codes (hypo/hyper/nodule/tc/other).
  // Silently falls back to the hardcoded numbers on any fetch failure, so
  // a network hiccup never leaves the page showing blank pricing.
  const [livePricing, setLivePricing] = useState(null);

  useEffect(() => {
    publicAPI.getPricing().then(r => setLivePricing(r.pricing || null)).catch(() => {});
  }, []);

  // hypo and hyper share the same price by design (Nuclear's original
  // 699/699/899/999 batch) — the "disorder" card covers both with one
  // figure, so hypo's live price stands in for the pair here.
  const priceFor = (opt) => {
    const shortCode = opt.key === 'disorder' ? 'hypo' : opt.key === 'nodule' ? 'nodule' : opt.key === 'other' ? 'other' : 'tc';
    const live = livePricing && livePricing[shortCode];
    return live
      ? { mrp: Math.round(live.mrpPaise / 100), offer: Math.round(live.offerPaise / 100) }
      : { mrp: opt.mrp, offer: opt.offer };
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg, var(--surface))' }}>
      <style>{`
        @keyframes tcFlapLeft { 0%, 100% { transform: rotateY(0deg); } 50% { transform: rotateY(-34deg); } }
        @keyframes tcFlapRight { 0%, 100% { transform: rotateY(0deg); } 50% { transform: rotateY(34deg); } }
        .tc-wing-l { animation: tcFlapLeft 2.6s ease-in-out infinite; transform-origin: right center; transform-style: preserve-3d; }
        .tc-wing-r { animation: tcFlapRight 2.6s ease-in-out infinite; transform-origin: left center; transform-style: preserve-3d; }
        @media (prefers-reduced-motion: reduce) {
          .tc-wing-l, .tc-wing-r { animation: none; }
        }
        @media (max-width: 760px) {
          .tc-options-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ── Nav ── */}
      <header style={{ borderBottom: '1px solid var(--border)' }}>
        <div style={{
          maxWidth: 1200, margin: '0 auto', padding: '20px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <MascotLogo height={44} fontSize={20} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {/* English / हिन्दी (13 Oct 2026) — chosen here, kept through registration and the dashboard */}
            <LanguageSwitcher />
            <Link to="/login" className="btn btn-secondary btn-sm">{t('Login')}</Link>
          </div>
        </div>
      </header>

      {/* ── Hero: butterfly mark only, no hero copy ── */}
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: '64px 24px 48px', textAlign: 'center' }}>
        <h1 style={{
          position: 'absolute', width: 1, height: 1, overflow: 'hidden',
          clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap',
        }}>
          {t('ThyroConsult — Online Thyroid Opinion')}
        </h1>
        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', perspective: 700 }}
          role="img" aria-label={t('ThyroConsult jeweled butterfly mark')}>
          <img className="tc-wing-l" src={wingLeft} alt="" style={{ height: 150, width: 'auto', display: 'block', marginRight: -1 }} />
          <img className="tc-wing-r" src={wingRight} alt="" style={{ height: 150, width: 'auto', display: 'block', marginLeft: -1 }} />
        </div>
      </section>

      {view === 'main' ? (
        <>
          {/* ── Option cards (3 thyroid conditions) ── */}
          <section
            className="tc-options-grid"
            style={{
              maxWidth: 1200, margin: '0 auto', padding: '0 24px 8px',
              display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20,
            }}
          >
            {MAIN_OPTIONS.map(opt => (
              <OptionCard key={opt.key} opt={opt} price={priceFor(opt)} expandedDisorder={expandedDisorder} setExpandedDisorder={setExpandedDisorder} />
            ))}
          </section>

          {/* ── "Other Problems" hand-off line ── */}
          {/* Bottom gap 80 → 16 px so the page fits without a scroll bar (13 Oct 2026) */}
          <p style={{ textAlign: 'center', fontSize: 14, color: 'var(--text-secondary)', padding: '16px 24px 16px', margin: 0 }}>
            {t('For "Other Problems"')}{' '}
            <button
              type="button"
              onClick={() => setView('other')}
              style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'var(--teal-600)', textDecoration: 'underline', cursor: 'pointer' }}
            >
              {t('click here')}
            </button>
          </p>
        </>
      ) : (
        <>
          {/* ── "Other Problems" — single card, reached via the line above ── */}
          <section style={{ maxWidth: 380, margin: '0 auto', padding: '16px 24px 8px' }}>
            <OptionCard opt={OTHER_OPTION} price={priceFor(OTHER_OPTION)} expandedDisorder={expandedDisorder} setExpandedDisorder={setExpandedDisorder} />
          </section>
          <div style={{ maxWidth: 380, margin: '0 auto', padding: '16px 24px 16px', textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => setView('main')}
              style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'underline', cursor: 'pointer' }}
            >
              {t('← Back')}
            </button>
          </div>
        </>
      )}

      {/* Policies, Contact Us and the operator's name on the home page
          (1 Oct 2026 — payment gateway's website check). */}
      <LegalFooter />
    </div>
  );
};

export default LandingPage;
